import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/components/feature/MovieCard';
import { getMovieById, getRelatedMovies } from '@/mocks/movies';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const movie = getMovieById(Number(id));

  if (!movie) {
    return (
      <main className="min-h-screen bg-background-50">
        <Navbar />
        <div className="flex flex-col items-center justify-center py-32">
          <div className="w-20 h-20 flex items-center justify-center rounded-full bg-background-100 mb-4">
            <i className="ri-error-warning-line text-3xl text-foreground-300"></i>
          </div>
          <h1 className="text-xl font-heading font-bold text-foreground-800 mb-2">内容未找到</h1>
          <p className="text-foreground-400 mb-6">该影视内容不存在或已被移除</p>
          <Link
            to="/"
            className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-xl text-sm font-medium transition-all cursor-pointer"
          >
            返回首页
          </Link>
        </div>
        <Footer />
      </main>
    );
  }

  const relatedMovies = getRelatedMovies(movie, 6);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Movie',
    name: movie.title,
    description: movie.description,
    director: {
      '@type': 'Person',
      name: movie.director,
    },
    actor: movie.actors.map((a) => ({
      '@type': 'Person',
      name: a,
    })),
    dateCreated: `${movie.year}`,
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: movie.rating,
      bestRating: 10,
    },
    image: movie.poster,
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <main className="min-h-screen bg-background-50">
        <Navbar />
        <Breadcrumb />

        <article className="w-full px-4 md:px-6 py-6 md:py-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="lg:w-80 shrink-0">
                <div className="aspect-[2/3] rounded-xl overflow-hidden bg-background-200/60 sticky top-24">
                  <img
                    src={movie.poster}
                    alt={movie.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                  {movie.title}
                </h1>

                <div className="flex flex-wrap items-center gap-2 mb-5">
                  <span className="px-3 py-1 bg-primary-500/15 text-primary-500 text-xs font-semibold rounded-full">
                    {movie.rating} 分
                  </span>
                  <span className="text-sm text-foreground-400">{movie.year}</span>
                  <span className="text-foreground-300">·</span>
                  <span className="text-sm text-foreground-400">{movie.duration}</span>
                  <span className="text-foreground-300">·</span>
                  <span className="text-sm text-foreground-400">
                    {movie.type === 'movie' ? '电影' : '电视剧'}
                  </span>
                  {movie.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2 py-0.5 bg-secondary-100/50 text-secondary-600 text-xs rounded-md border border-secondary-200/40"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="bg-background-100/60 rounded-xl p-6 mb-6">
                  <div className="aspect-video rounded-lg bg-background-200/60 flex items-center justify-center mb-4 overflow-hidden">
                    <img
                      src={movie.poster}
                      alt={movie.title}
                      className="w-full h-full object-cover opacity-60"
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-16 h-16 flex items-center justify-center rounded-full bg-primary-500/90 backdrop-blur-sm cursor-pointer hover:bg-primary-600 transition-all">
                        <i className="ri-play-fill text-3xl text-background-50 ml-1"></i>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-4 mb-6">
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-700 mb-2">剧情简介</h3>
                    <p className="text-sm text-foreground-400 leading-relaxed">{movie.description}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <span className="text-xs text-foreground-300">导演</span>
                      <p className="text-sm text-foreground-600 font-medium mt-0.5">{movie.director}</p>
                    </div>
                    <div>
                      <span className="text-xs text-foreground-300">类型</span>
                      <p className="text-sm text-foreground-600 font-medium mt-0.5">
                        {movie.type === 'movie' ? '电影' : '电视剧'}
                      </p>
                    </div>
                    <div>
                      <span className="text-xs text-foreground-300">年份</span>
                      <p className="text-sm text-foreground-600 font-medium mt-0.5">{movie.year}</p>
                    </div>
                    <div>
                      <span className="text-xs text-foreground-300">时长</span>
                      <p className="text-sm text-foreground-600 font-medium mt-0.5">{movie.duration}</p>
                    </div>
                  </div>

                  <div>
                    <span className="text-xs text-foreground-300">主演</span>
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {movie.actors.map((actor) => (
                        <span
                          key={actor}
                          className="px-2.5 py-0.5 bg-background-100 border border-background-200/40 text-sm text-foreground-500 rounded-md"
                        >
                          {actor}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {relatedMovies.length > 0 && (
              <section className="mt-12">
                <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950 mb-5">
                  相关推荐
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                  {relatedMovies.map((m) => (
                    <MovieCard key={m.id} movie={m} />
                  ))}
                </div>
              </section>
            )}
          </div>
        </article>

        <Footer />
      </main>
    </>
  );
}